import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import { trackEvent } from '../analyticsLoader';
import { useToast } from '../context/ToastContext';
const LS_KEY = 'favoritePropertyIds';

// ─── localStorage helpers (guest fallback) ───────────────────────────────────

function readLocalIds() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function writeLocalIds(ids) {
  localStorage.setItem(LS_KEY, JSON.stringify(ids));
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useFavorites()
 *
 * When the user is authenticated → favorites are persisted to the API
 * (GET / POST / DELETE /user/favorites).
 * When the user is a guest → falls back to localStorage only.
 *
 * On login, any locally-saved IDs are merged into the server list once.
 */
export function useFavorites() {
  const { user, authedFetch } = useAuth();
  const { showToast } = useToast();
  const [favoriteIds, setFavoriteIds] = useState(() => readLocalIds());
  const [syncing, setSyncing] = useState(false);
  const mergedRef = useRef(false); // track one-time merge on login

  // ── Load from API when user logs in ────────────────────────────────────────
  useEffect(() => {
    if (!user) {
      // Logged out: reset to local
      mergedRef.current = false;
      setFavoriteIds(readLocalIds());
      return;
    }

    let cancelled = false;
    setSyncing(true);

    authedFetch(`${API_BASE}/user/favorites`)
      .then((r) => r.json())
      .then(async (data) => {
        if (cancelled) {
          return;
        }
        const serverIds = Array.isArray(data.favorites) ? data.favorites.map(String) : [];

        // One-time: merge any guest IDs that aren't already on the server
        if (!mergedRef.current) {
          mergedRef.current = true;
          const localIds = readLocalIds();
          const toMerge = localIds.filter((id) => !serverIds.includes(id));
          const confirmed = [...serverIds];
          // Ids the server explicitly rejected (e.g. the property no longer
          // exists) are dropped for good; a network failure just leaves the
          // id untouched in localStorage so a later sync can retry it -
          // these need separate handling since one should stop retrying
          // forever and the other shouldn't give up after one bad request.
          const retryLater = [];
          for (const id of toMerge) {
            try {
              const response = await authedFetch(`${API_BASE}/user/favorites`, {
                method: 'POST',
                body: JSON.stringify({ propertyId: id }),
              });
              if (response.ok) {
                confirmed.push(id);
              }
            } catch {
              retryLater.push(id);
            }
          }
          const merged = [...new Set(confirmed)];
          const toPersist = [...new Set([...confirmed, ...retryLater])];
          if (!cancelled) {
            setFavoriteIds(merged);
            writeLocalIds(toPersist);
          }
        } else {
          setFavoriteIds(serverIds);
          writeLocalIds(serverIds);
        }
      })
      .catch(() => {
        // Network error — fall back to local
        if (!cancelled) {
          setFavoriteIds(readLocalIds());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSyncing(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ── Toggle ──────────────────────────────────────────────────────────────────
  const toggleFavorite = useCallback(
    async (propertyId) => {
      const key = String(propertyId);
      const isCurrentlyFav = favoriteIds.includes(key);
      const next = isCurrentlyFav ? favoriteIds.filter((id) => id !== key) : [...favoriteIds, key];

      if (!isCurrentlyFav) {
        trackEvent('favorite_added', { property_id: key });
      }

      // Optimistic local update
      setFavoriteIds(next);
      writeLocalIds(next);

      if (!user) {
        return;
      } // guest — local only

      try {
        const response = isCurrentlyFav
          ? await authedFetch(`${API_BASE}/user/favorites/${key}`, { method: 'DELETE' })
          : await authedFetch(`${API_BASE}/user/favorites`, {
            method: 'POST',
            body: JSON.stringify({ propertyId: key }),
          });

        // fetch() only rejects on network failure, not HTTP error status -
        // without this check a 404/500 silently left the optimistic update
        // in place, showing "favorited" for something that was never saved.
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to update favorites');
        }
      } catch (err) {
        // Roll back on network failure or a non-ok response
        setFavoriteIds(favoriteIds);
        writeLocalIds(favoriteIds);
        showToast(err.message || 'Failed to update favorites', 'error');
      }
    },
    [favoriteIds, user, authedFetch],
  );

  const isFavorite = useCallback(
    (propertyId) => favoriteIds.includes(String(propertyId)),
    [favoriteIds],
  );

  return { favoriteIds, isFavorite, toggleFavorite, syncing };
}
